import { ALPHANUMERIC_REGEX } from "common/constants";
import { escapeForRegExp } from "common/utils/strings";

export const HOME_PLAN_NAME_LENGTH_MAX = 28;
export const PLAN_NAME_LENGTH_MAX = 255;

const HOME_PLAN_NAME_SUFFIX_LENGTH_ROOM = 7;
const DEFAULT_HOME_NAME = "DefaultHome";

export enum GeneratedPlanNameType {
  User = "User",
  GenLot = "GenLot",
  GenNoLot = "GenNoLot",
  Fallback = "Fallback",
}

/**
 * Generates a home plan name.
 *
 * Constraints:
 *  - Length of 28 characters or less (to fit on generated PDFs)
 *
 * Order of precedence:
 *  1. Provided plan name
 *  2. Community name, abbreviated if needed, with lot number (e.g. Community-L1)
 *  3. Community name, abbreviated if needed, postfixed with -P
 *
 * If a generated plan name is too long, it will be replaced by a shorter version, where the suffix (e.g -P or -L1)
 * is preserved and the rest of the name shortened. Example:
 *
 * Thisstringiswaytoolongtobeaplanname-L134
 *
 * would become:
 *
 * Thisstringiswaytoo-L134
 */
export function generateHomePlanName(
  desiredPlanName: string | null | undefined,
  communityName?: string | null,
  lotNumber?: string | null,
): { name: string; type: GeneratedPlanNameType } {
  if (desiredPlanName?.trim()) {
    return { name: trimNameToMax(desiredPlanName), type: GeneratedPlanNameType.User };
  } else if (communityName) {
    const abbv = abbreviatedCommunityName(communityName);
    if (lotNumber) {
      return { name: `${abbv}-L${lotNumber}`, type: GeneratedPlanNameType.GenLot };
    }
    return { name: `${abbv}-P`, type: GeneratedPlanNameType.GenNoLot };
  }
  return { name: DEFAULT_HOME_NAME, type: GeneratedPlanNameType.Fallback };
}

export function generateNextHomePlanName(
  generatedName: string,
  generatedType: GeneratedPlanNameType,
  currentIndex: number,
): string {
  if (generatedType === GeneratedPlanNameType.GenNoLot) {
    return `${generatedName}${currentIndex + 1}`;
  }
  if (currentIndex === 0) {
    return generatedName;
  }
  return `${generatedName}-${currentIndex}`;
}

export function parseMaxIndexFromNames(generatedName: string, existingNames: (string | null)[]): number {
  const re = new RegExp(`^${escapeForRegExp(generatedName)}(\\d+)(-\\d+)?$`);
  return existingNames.reduce((index, name) => {
    const match = name && re.exec(name);
    if (match?.length) {
      const i = parseInt(match[1]);
      if (i > index) {
        return i;
      }
    }
    return index;
  }, 0);
}

function trimNameToMax(name: string, max = HOME_PLAN_NAME_LENGTH_MAX) {
  name = name.trim();
  if (name.length > max) {
    return name.substring(0, max);
  }
  return name;
}

export function abbreviatedCommunityName(name: string) {
  name = name.trim();
  if (name.includes(" ")) {
    name = name
      .split(" ")
      .map((part) => {
        const alphaNumericChar = part.match(ALPHANUMERIC_REGEX) || [];
        return (alphaNumericChar[0] || part.charAt(0)).toUpperCase();
      })
      .join("");
  }
  /**
   * Coming in short of the max length makes room for additional suffix length
   * i.e. -P40 or -L5-999. This provides stability of the name component
   * as the suffix length grows.
   *
   * - TruncatedCommunityNam-L5-1
   * - TruncatedCommunityNam-L5-999
   *
   * Versus
   *
   * - TruncatedCommunityName-L5-1
   * - TruncatedCommunityNam-L5-11
   * - TruncatedCommunityNa-L5-111
   */
  return trimNameToMax(name, HOME_PLAN_NAME_LENGTH_MAX - HOME_PLAN_NAME_SUFFIX_LENGTH_ROOM);
}

// preserved for backwards-compatibility with old data
export function defaultPlanSetId(communityName: string, decodedHomePlanId: number): string {
  return `${communityName
    .split(" ")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("")}-${decodedHomePlanId}`;
}
